/**
 * Failed syndication reads, carried with the ErrorResponse code the server
 * sent so a page can word the failure for an FI operator.
 *
 * The syndication pages threw `new Error(error.message)`, which dropped the
 * code and printed the server's developer-facing sentence verbatim. The
 * 2026-09-27 admin.aaim.com sweep (fi-g9-1) captured Positions and
 * Indications both reading "Caller's tenant has multiple active CRT
 * participants; data-integrity issue." to an FI admin. The code is what a
 * page keys its wording on; the message stays the fallback for codes no page
 * words yet.
 */

import type { components as LspComponents } from '@aaimplatform/institutional-types/loan-syndication-protocol';

type ErrorResponse = LspComponents['schemas']['ErrorResponse'];

export class SyndicationReadError extends Error {
  constructor(
    message: string,
    public readonly code: string | undefined,
    public readonly status: number | undefined,
  ) {
    super(message);
    this.name = 'SyndicationReadError';
  }
}

/**
 * Build the error a query function throws from an openapi-fetch failure.
 * `error` is the parsed body (an ErrorResponse on every syndication route),
 * `status` the HTTP status, `fallback` the message when the body carries none.
 */
export function toSyndicationReadError(
  error: unknown,
  status: number | undefined,
  fallback: string,
): SyndicationReadError {
  const body = error && typeof error === 'object' ? (error as Partial<ErrorResponse>) : {};
  const code = typeof body.error === 'string' ? body.error : undefined;
  const message = typeof body.message === 'string' && body.message ? body.message : fallback;
  return new SyndicationReadError(message, code, status);
}

/**
 * The two participant-context refusals from loan-syndication-protocol
 * middleware/auth.ts getParticipantContextForTenant. Both are conditions of
 * the institution's network record, not of the request, so they get
 * operator wording and a support path rather than the server sentence.
 */
export const PARTICIPANT_CONTEXT_COPY: Readonly<
  Record<string, { reason: string; supportItem: string }>
> = {
  AMBIGUOUS_PARTICIPANT_CONTEXT: {
    reason:
      "Your institution's Loan Participations network membership record needs attention before this list can load.",
    supportItem: 'Loan Participations network membership record',
  },
  NO_PARTICIPANT_FOR_TENANT: {
    reason:
      'Your institution has not completed Loan Participations network onboarding, so this list cannot load yet.',
    supportItem: 'Loan Participations network onboarding',
  },
};

/** The operator wording for a participant-context refusal, or null for any other failure. */
export function participantContextCopy(
  error: unknown,
): { code: string; reason: string; supportItem: string } | null {
  if (!(error instanceof SyndicationReadError) || !error.code) return null;
  const copy = PARTICIPANT_CONTEXT_COPY[error.code];
  return copy ? { code: error.code, ...copy } : null;
}
